import { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import {
  submitOrganizationDocuments,
  IOrganizationDocumentUpload,
} from "../../setup/api/services/organization";
import styles from "./styles.module.scss";
import { CheckIcon, ReviewDocIcon, UploadIcon } from "./gateIcons";
import {
  ACCEPTED_DOCUMENT_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
  ORGANIZATION_DOCUMENT_SLOTS,
} from "./organizationDocuments.constants";

/** What the pending screen needs to list the documents back to the user. */
export interface ISubmittedDocument {
  type: string;
  labelKey: string;
  fileName: string;
}

interface Props {
  /** Organization the documents attach to. */
  organizationCode: string;
  onSubmitted: (documents: ISubmittedDocument[]) => void;
}

/**
 * Onboarding step between self-registration and the review-status screen: the business
 * uploads the documents required before an admin will review the organization.
 *
 * Each file goes to the bucket via a presigned PUT and is then registered as an organization
 * attachment — see submitOrganizationDocuments.
 */
const OrganizationDocuments: React.FC<Props> = ({
  organizationCode,
  onSubmitted,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<Record<string, File>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPick = (type: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file after an error still fires onChange.
    e.target.value = "";
    if (!file) return;

    if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
      setFieldErrors((prev) => ({
        ...prev,
        [type]: t("lbl_org_docs_invalid_type"),
      }));
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      setFieldErrors((prev) => ({
        ...prev,
        [type]: t("lbl_org_docs_too_large"),
      }));
      return;
    }

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
    setFiles((prev) => ({ ...prev, [type]: file }));
  };

  // Every document is mandatory — submission stays blocked until all slots are filled.
  const canSubmit = ORGANIZATION_DOCUMENT_SLOTS.every(
    ({ type }) => !!files[type]
  );

  const onSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const uploads: IOrganizationDocumentUpload[] =
        ORGANIZATION_DOCUMENT_SLOTS.map(({ type, labelKey }) => ({
          type,
          // The label doubles as the attachment title the admin reviewer sees.
          title: t(labelKey),
          file: files[type],
        }));
      await submitOrganizationDocuments(organizationCode, uploads);
      onSubmitted(
        ORGANIZATION_DOCUMENT_SLOTS.map(({ type, labelKey }) => ({
          type,
          labelKey,
          fileName: files[type].name,
        }))
      );
    } catch {
      setError(t("lbl_org_docs_submit_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.docsCard}>
      <h3>{t("lbl_org_docs_title")}</h3>
      <p>{t("lbl_org_docs_subtitle")}</p>

      <div className={styles.docList}>
        {ORGANIZATION_DOCUMENT_SLOTS.map(({ type, labelKey }) => {
          const file = files[type];
          const fieldError = fieldErrors[type];
          return (
            <label
              key={type}
              className={classNames(styles.docUpload, {
                [styles.hasFile]: !!file,
                [styles.hasError]: !!fieldError,
              })}>
              <input
                type="file"
                accept={ACCEPTED_DOCUMENT_TYPES.join(",")}
                hidden
                onChange={onPick(type)}
              />
              <span className={styles.duIcon}>
                <ReviewDocIcon />
              </span>
              <span className={styles.duTxt}>
                <b>{t(labelKey)}</b>
                {fieldError ? (
                  <span className={styles.duError}>{fieldError}</span>
                ) : file ? (
                  // dir="auto" so a Latin file name ellipsizes at its own end.
                  <span className={styles.duFile} dir="auto">
                    {file.name}
                  </span>
                ) : (
                  <span className={styles.duHint}>
                    {t("lbl_org_docs_hint")}
                  </span>
                )}
              </span>
              <span className={styles.duCta}>
                <UploadIcon />
                {file ? t("lbl_org_docs_change") : t("lbl_org_docs_upload")}
              </span>
              {file && (
                <span className={styles.duDone}>
                  <CheckIcon />
                </span>
              )}
            </label>
          );
        })}
      </div>

      {error && <p className={styles.docsError}>{error}</p>}

      <div className={styles.docsActions}>
        <button
          type="button"
          disabled={!canSubmit || submitting}
          className={classNames({ [styles.isLoading]: submitting })}
          onClick={onSubmit}>
          {submitting ? (
            <span className={styles.spinner} />
          ) : (
            t("lbl_org_docs_submit")
          )}
        </button>
      </div>
    </div>
  );
};

export default OrganizationDocuments;
